import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { HashRouter, Routes, Route, useLocation } from "react-router-dom";
import { MotionConfig } from "framer-motion";
import { useEffect } from "react";
import { ROUTE_PATHS } from "@/lib/index";
import { Navigate } from "react-router-dom";
import { Layout } from "@/components/Layout";
import { AdminGuard } from "@/components/AdminGuard";
import AdminLogin from "@/pages/admin/AdminLogin";
import AdminDashboard from "@/pages/admin/AdminDashboard";
import AdminBOM from "@/pages/admin/AdminBOM";
// Admin sub-pages (rendered inside AdminDashboard via tab routing)
import type {} from "@/pages/admin/AdminRecipes";
import type {} from "@/pages/admin/AdminOrders";
import type {} from "@/pages/admin/AdminLogistics";
import type {} from "@/pages/admin/AdminProduction";
import type {} from "@/pages/admin/AdminCustomers";
import type {} from "@/pages/admin/AdminTracker";
import type {} from "@/pages/admin/AdminOverview";
import type {} from "@/pages/home/Index";
import type {} from "@/pages/home/components/Demo";
import type {} from "@/pages/not-found/Index";
import Home from "@/pages/Home";
import Products from "@/pages/Products";
import Capabilities from "@/pages/Capabilities";
import Industries from "@/pages/Industries";
import Selection from "@/pages/Selection";
import About from "@/pages/About";
import Contact from "@/pages/Contact";
import Guide from "@/pages/Guide";
import ProductFKM from "@/pages/ProductFKM";
import ProductEPDM from "@/pages/ProductEPDM";
import ProductNBR from "@/pages/ProductNBR";
import ProductNR from "@/pages/ProductNR";
import ProductESD from "@/pages/ProductESD";
import ProductCR from "@/pages/ProductCR";
import TechSpecs from "@/pages/TechSpecs";

const queryClient = new QueryClient();

function ScrollToTop(): null {
  const { pathname } = useLocation();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);

  return null;
}

const App = () => (
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <MotionConfig reducedMotion="user">
        <Toaster />
        <Sonner />
        <HashRouter>
          <ScrollToTop />
          <Routes>
            <Route
              path={ROUTE_PATHS.HOME}
              element={
                <Layout>
                  <Home />
                </Layout>
              }
            />
            <Route
              path={ROUTE_PATHS.PRODUCTS}
              element={
                <Layout>
                  <Products />
                </Layout>
              }
            />
            <Route
              path={ROUTE_PATHS.CAPABILITIES}
              element={
                <Layout>
                  <Capabilities />
                </Layout>
              }
            />
            <Route
              path={ROUTE_PATHS.INDUSTRIES}
              element={
                <Layout>
                  <Industries />
                </Layout>
              }
            />
            <Route
              path={ROUTE_PATHS.SELECTION}
              element={
                <Layout>
                  <Selection />
                </Layout>
              }
            />
            <Route
              path={ROUTE_PATHS.ABOUT}
              element={
                <Layout>
                  <About />
                </Layout>
              }
            />
            <Route
              path={ROUTE_PATHS.CONTACT}
              element={
                <Layout>
                  <Contact />
                </Layout>
              }
            />
            <Route
              path={ROUTE_PATHS.GUIDE}
              element={
                <Layout>
                  <Guide />
                </Layout>
              }
            />
            <Route
              path={ROUTE_PATHS.PRODUCT_FKM}
              element={
                <Layout>
                  <ProductFKM />
                </Layout>
              }
            />
            <Route
              path={ROUTE_PATHS.PRODUCT_EPDM}
              element={
                <Layout>
                  <ProductEPDM />
                </Layout>
              }
            />
            <Route
              path={ROUTE_PATHS.PRODUCT_NBR}
              element={
                <Layout>
                  <ProductNBR />
                </Layout>
              }
            />
            <Route
              path={ROUTE_PATHS.PRODUCT_NR}
              element={
                <Layout>
                  <ProductNR />
                </Layout>
              }
            />
            <Route
              path={ROUTE_PATHS.PRODUCT_ESD}
              element={
                <Layout>
                  <ProductESD />
                </Layout>
              }
            />
            <Route
              path={ROUTE_PATHS.PRODUCT_CR}
              element={
                <Layout>
                  <ProductCR />
                </Layout>
              }
            />
            <Route
              path={ROUTE_PATHS.TECH_SPECS}
              element={
                <Layout>
                  <TechSpecs />
                </Layout>
              }
            />
            {/* 管理后台路由（不使用公开 Layout） */}
            {/* /login 是对外隐蔽入口，重定向到 /admin/login */}
            <Route path="/login" element={<Navigate to={ROUTE_PATHS.ADMIN_LOGIN} replace />} />
            <Route
              path={ROUTE_PATHS.ADMIN_LOGIN}
              element={<AdminLogin />}
            />
            <Route
              path={ROUTE_PATHS.ADMIN_DASHBOARD}
              element={
                <AdminGuard allowedRoles={['sales', 'admin']}>
                  <AdminDashboard />
                </AdminGuard>
              }
            />
            <Route
              path={ROUTE_PATHS.ADMIN_BOM}
              element={
                <AdminGuard allowedRoles={['factory', 'admin']}>
                  <AdminBOM />
                </AdminGuard>
              }
            />
            {/* 订单、物流、生产、仪表盘 — 全部路由统一渲染 AdminDashboard，切换 tab 由内部导航控制 */}
            <Route
              path={ROUTE_PATHS.ADMIN_ORDERS}
              element={
                <AdminGuard allowedRoles={['sales', 'admin']}>
                  <AdminDashboard />
                </AdminGuard>
              }
            />
            <Route
              path={ROUTE_PATHS.ADMIN_PRODUCTION}
              element={
                <AdminGuard allowedRoles={['factory', 'admin']}>
                  <AdminDashboard />
                </AdminGuard>
              }
            />
            <Route
              path={ROUTE_PATHS.ADMIN_LOGISTICS}
              element={
                <AdminGuard allowedRoles={['sales', 'admin']}>
                  <AdminDashboard />
                </AdminGuard>
              }
            />
            <Route
              path={ROUTE_PATHS.ADMIN_OVERVIEW}
              element={
                <AdminGuard allowedRoles={['admin']}>
                  <AdminDashboard />
                </AdminGuard>
              }
            />
          </Routes>
        </HashRouter>
      </MotionConfig>
    </TooltipProvider>
  </QueryClientProvider>
);

export default App;
